import { useState } from "react";
import { Heart, CheckCircle2 } from "lucide-react";
import { submitCustomOrder } from "../lib/api";

const initial = {
    name: "",
    email: "",
    phone: "",
    event_date: "",
    occasion: "",
    flavor_preferences: "",
    color_preferences: "",
    budget: "",
    notes: "",
};

const FIELD = "w-full rounded-2xl border border-[var(--sss-border)] bg-white px-4 py-3 text-sm focus:outline-none focus:border-[var(--sss-pink)] focus:ring-2 focus:ring-[var(--sss-pink)]/20 transition";

export default function CustomOrders() {
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            const res = await submitCustomOrder(form);
            setSuccess(res);
            setForm(initial);
        } catch (err) {
            setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <div className="max-w-2xl mx-auto px-4 md:px-8 py-20 text-center" data-testid="custom-order-success">
                <div className="mx-auto w-16 h-16 rounded-full bg-[var(--sss-blush)] flex items-center justify-center mb-4">
                    <CheckCircle2 size={32} className="text-[var(--sss-pink)]" strokeWidth={1.5} />
                </div>
                <h2 className="font-serif-display text-4xl font-bold">Thank you, {success.name.split(" ")[0]}!</h2>
                <p className="mt-3 text-[var(--sss-text-muted)]">
                    Your custom order request has been received. We&apos;ll reach out within 24–48 hours to confirm details and pricing.
                </p>
                <p className="mt-2 text-sm text-[var(--sss-text-muted)]">
                    Reference: <span className="font-mono">{success.id}</span>
                </p>
                <button type="button" onClick={() => setSuccess(null)} className="btn-pink mt-8" data-testid="custom-order-new">
                    Submit Another Request
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-14" data-testid="custom-orders-page">
            <div className="text-center">
                <p className="text-xs uppercase tracking-[0.3em] text-[var(--sss-pink)] font-semibold">For Any Occasion</p>
                <h1 className="font-serif-display text-5xl font-bold mt-3">Custom Orders</h1>
                <p className="mt-4 text-[var(--sss-text-muted)] max-w-xl mx-auto">
                    Beautiful, custom treats for birthdays, weddings, baby showers, corporate events, holidays & more.
                </p>
                <p className="mt-3 inline-block bg-[var(--sss-blush)] text-[var(--sss-pink)] font-serif-display italic px-4 py-1.5 rounded-full text-sm">
                    Minimum order starts at $150
                </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-10 bg-white rounded-3xl sss-shadow p-6 md:p-10 space-y-5" data-testid="custom-orders-form">
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="co-name">Full Name *</label>
                        <input id="co-name" required value={form.name} onChange={update("name")} className={FIELD + " mt-1"} data-testid="co-name" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="co-email">Email *</label>
                        <input id="co-email" type="email" required value={form.email} onChange={update("email")} className={FIELD + " mt-1"} data-testid="co-email" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="co-phone">Phone *</label>
                        <input id="co-phone" required value={form.phone} onChange={update("phone")} className={FIELD + " mt-1"} data-testid="co-phone" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="co-date">Event Date</label>
                        <input id="co-date" type="date" value={form.event_date} onChange={update("event_date")} className={FIELD + " mt-1"} data-testid="co-event-date" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="co-occasion">Occasion</label>
                        <select id="co-occasion" value={form.occasion} onChange={update("occasion")} className={FIELD + " mt-1"} data-testid="co-occasion">
                            <option value="">Select an occasion…</option>
                            <option>Birthday</option>
                            <option>Wedding</option>
                            <option>Baby Shower</option>
                            <option>Corporate Event</option>
                            <option>Holiday</option>
                            <option>Other</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="co-budget">Budget</label>
                        <select id="co-budget" value={form.budget} onChange={update("budget")} className={FIELD + " mt-1"} data-testid="co-budget">
                            <option value="">Select a range…</option>
                            <option>$150 – $250</option>
                            <option>$250 – $500</option>
                            <option>$500 – $1,000</option>
                            <option>$1,000+</option>
                        </select>
                    </div>
                </div>

                <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="co-flavors">Flavor Preferences</label>
                    <input id="co-flavors" placeholder="Watermelon, Strawberry, Blue Raspberry…" value={form.flavor_preferences} onChange={update("flavor_preferences")} className={FIELD + " mt-1"} data-testid="co-flavors" />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="co-colors">Color Preferences</label>
                    <input id="co-colors" placeholder="Pink, Red, Purple…" value={form.color_preferences} onChange={update("color_preferences")} className={FIELD + " mt-1"} data-testid="co-colors" />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="co-notes">Additional Notes</label>
                    <textarea id="co-notes" rows={4} value={form.notes} onChange={update("notes")} className={FIELD + " mt-1 resize-none"} placeholder="Tell us about your vision, theme, dietary needs…" data-testid="co-notes" />
                </div>

                {error && (
                    <p className="text-sm text-red-600" data-testid="co-error">{error}</p>
                )}

                <button type="submit" disabled={submitting} className="btn-pink w-full justify-center" data-testid="co-submit">
                    {submitting ? "Sending…" : <>Submit Request <Heart size={14} fill="#fff" strokeWidth={0} /></>}
                </button>
            </form>
        </div>
    );
}
